import { istRechercheSperre } from '../../utils/errorHandler';
import { EBENEN, MENUE } from '../../config/ebenen';
import { MAX_UPLOAD_BYTES, uploadProblem } from '../feedbackUpload';
import type { TicketAktionen, TicketAnsicht, TicketNachricht } from './hilfeApi';
import { erfolgMitMail, erfolgOhneMail, folgeBetreff, mitgeschickt, TICKET_TEXTE, wartetSeit } from './ticketTexte';

// Reine Funktionen der Tickets der Person (Profil-Nachbau F-06, BAU-HILFE.md 1.5 und 3.2; PF-310-1), ohne JSX und ohne Browser
// testbar (tests/hilfe-tickets.test.mjs). Die Grenzen sind dieselben wie beim Server (backend/src/support/tickets.ts,
// anhaenge.ts); der Server entscheidet, diese Prüfung erspart nur einen vergeblichen Versand.

export const TICKET_STATUS = ['NEU', 'IN_BEARBEITUNG', 'WARTET_AUF_KUNDE', 'GELOEST', 'GESCHLOSSEN'] as const;
export type TicketStatus = (typeof TICKET_STATUS)[number];
// Spiegel von KATEGORIEN in backend/src/support/regeln.ts (gleiche Werte, gleiche Reihenfolge).
export const KATEGORIEN = ['FEHLER', 'ANTWORT_FALSCH', 'FRAGE', 'KONTO', 'ABRECHNUNG', 'WUNSCH', 'SONSTIGES'] as const;
export type Kategorie = (typeof KATEGORIEN)[number];
// Spiegel von FEHLERCODES in backend/src/support/regeln.ts (gleiche Werte, gleiche Reihenfolge; Ratsche in hilfe-tickets.test.mjs).
export const FEHLERCODES = ['COST_LIMIT', 'RESEARCH_LIMIT', 'SEAT_REQUIRED', 'ORGANIZATION_BLOCKED', 'PAYMENT_OVERDUE', 'SUBSCRIPTION_EXPIRED',
  'TWO_FACTOR_SETUP_REQUIRED', 'QUERY_BLOCKED', 'RESEARCH_FAILED'] as const;
export type Fehlercode = (typeof FEHLERCODES)[number];

/** Statuswörter der Kundenseite (TEIL-3 1.0). */
export const STATUS_WORT: Record<TicketStatus, string> = TICKET_TEXTE.status;
export const KATEGORIE_WORT: Record<Kategorie, string> = TICKET_TEXTE.kategorie;

const FARBE: Record<TicketStatus, string> = { NEU: '--cl-link', IN_BEARBEITUNG: '--cl-warning-ink', WARTET_AUF_KUNDE: '--cl-danger-ink',
  GELOEST: '--cl-success-ink', GESCHLOSSEN: '--cl-muted' };
/** Token der Farbe des Statuspunkts (TEIL-3 1.0); unbekannt wie „Geschlossen“. */
export const statusFarbe = (status: string) => FARBE[status as TicketStatus] ?? FARBE.GESCHLOSSEN;
export const statusWort = (status: string) => STATUS_WORT[status as TicketStatus] ?? status;
export const kategorieWort = (kategorie: string) => KATEGORIE_WORT[kategorie as Kategorie] ?? kategorie;

export const BETREFF = { min: 3, max: 150 } as const;
export const BESCHREIBUNG = { min: 10, max: 10000 } as const;
export const MAX_DATEIEN = 3;
export const MAX_DATEIEN_BYTES = 20 * 1024 * 1024;

const SEITE = /^\/[A-Za-z0-9/_-]*$/;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
export const TICKET_REFERENZ = /^SUP-[23456789ABCDEFGHJKLMNPQRSTUVWXYZ]{7}$/;

export type Vorbelegung = {
  seite?: string;
  fehlercode?: Fehlercode;
  kategorie?: Kategorie;
  /** F-23 (PF-NB-hilfe-3): ?recherche=<uuid> aus „Dem Support melden“ an einer Antwort. */
  rechercheId?: string;
  stoerungId?: string;
  folge?: string;
  betreff?: string;
  /** aus=chat mit gültiger Unterhaltung: Entwurf laden, Verlauf anbieten. */
  unterhaltungId?: string;
};

/** Vorbelegung aus der Abfrage des Formulars; ungültige Werte fallen still weg. Ein Fehlercode setzt „Etwas funktioniert nicht“. */
export function vorbelegung(search: string): Vorbelegung {
  const p = new URLSearchParams(search);
  const v: Vorbelegung = {};
  const seite = p.get('seite');
  if (seite !== null && seite.length <= 300 && SEITE.test(seite)) v.seite = seite;
  // F-23 (PF-NB-hilfe-3): ?kategorie= aus der Liste; ?recherche= mit gültiger Kennung, dann ohne eigene Wahl ANTWORT_FALSCH. Ein
  // Fehlercode setzt weiter FEHLER (unten).
  const kategorie = p.get('kategorie');
  if (kategorie !== null && (KATEGORIEN as readonly string[]).includes(kategorie)) v.kategorie = kategorie as Kategorie;
  const recherche = p.get('recherche');
  if (recherche !== null && UUID.test(recherche)) { v.rechercheId = recherche.toLowerCase(); v.kategorie ??= 'ANTWORT_FALSCH'; }
  const code = p.get('fehlercode');
  if (code !== null && (FEHLERCODES as readonly string[]).includes(code)) { v.fehlercode = code as Fehlercode; v.kategorie = 'FEHLER'; }
  const stoerung = p.get('stoerung');
  if (stoerung !== null && UUID.test(stoerung)) v.stoerungId = stoerung.toLowerCase();
  const folge = p.get('folge');
  if (folge !== null && TICKET_REFERENZ.test(folge)) { v.folge = folge; v.betreff = folgeBetreff(folge); }
  const unterhaltung = p.get('unterhaltung');
  if (p.get('aus') === 'chat' && unterhaltung !== null && UUID.test(unterhaltung)) v.unterhaltungId = unterhaltung.toLowerCase();
  return v;
}

export type FormularWerte = { kategorie: Kategorie | ''; betreff: string; beschreibung: string };
export type FormularPruefung = {
  /** Namen der leeren Pflichtfelder für „Es fehlt noch: …“, in der Reihenfolge des Formulars. */
  fehlt: string[];
  leer: { kategorie: boolean; betreff: boolean; beschreibung: boolean };
  /** Fehler am Feld (Länge, Steuerzeichen). */
  felder: { betreff?: string; beschreibung?: string };
};
// Wie der Server: Steuerzeichen außer Tabulator, Zeilenumbruch und Wagenrücklauf sind nicht erlaubt.
const STEUERZEICHEN = /[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f]/;

function laenge(wert: string, grenze: { min: number; max: number }, satz: string): string | undefined {
  if (STEUERZEICHEN.test(wert)) return TICKET_TEXTE.fehler.steuerzeichen;
  const n = wert.trim().length;
  return n < grenze.min || n > grenze.max ? satz : undefined;
}

/** Prüft das Formular mit den Grenzen des Servers: leere Pflichtfelder nennt „Es fehlt noch“, alles andere steht am Feld. */
export function pruefeFormular(werte: FormularWerte): FormularPruefung {
  const leer = { kategorie: werte.kategorie === '', betreff: werte.betreff.trim() === '', beschreibung: werte.beschreibung.trim() === '' };
  const F = TICKET_TEXTE.formular.fehlt;
  const felder: FormularPruefung['felder'] = {};
  const betreff = leer.betreff ? undefined : laenge(werte.betreff, BETREFF, TICKET_TEXTE.fehler.betreffLaenge);
  const beschreibung = leer.beschreibung ? undefined : laenge(werte.beschreibung, BESCHREIBUNG, TICKET_TEXTE.fehler.beschreibungLaenge);
  if (betreff) felder.betreff = betreff;
  if (beschreibung) felder.beschreibung = beschreibung;
  const fehlt: string[] = [];
  if (leer.kategorie) fehlt.push(F.kategorie);
  if (leer.betreff) fehlt.push(F.betreff);
  if (leer.beschreibung) fehlt.push(F.beschreibung);
  return { fehlt, leer, felder };
}
export const formularGueltig = (p: FormularPruefung) => p.fehlt.length === 0 && Object.keys(p.felder).length === 0;

/** Das erste Problem einer Auswahl von Dateien, sonst null: Zahl, Typ (uploadProblem), Größe je Datei, Größe zusammen. */
export function dateiProbleme(dateien: { name: string; size: number }[]): string | null {
  if (dateien.length > MAX_DATEIEN) return TICKET_TEXTE.fehler.dateienZahl;
  for (const datei of dateien) {
    if (datei.size > MAX_UPLOAD_BYTES) return TICKET_TEXTE.fehler.dateiGroesse;
    const problem = uploadProblem(datei);
    if (problem) return problem;
  }
  if (dateien.reduce((summe, datei) => summe + datei.size, 0) > MAX_DATEIEN_BYTES) return TICKET_TEXTE.fehler.dateienGroesse;
  return null;
}

/** Neue Dateien zur Auswahl hinzu: jede für sich geprüft; was nicht passt, bleibt draußen und nennt den ersten Grund. */
export function dateienHinzu<T extends { name: string; size: number }>(vorhanden: T[], neue: T[]): { dateien: T[]; fehler: string | null } {
  let dateien = vorhanden, fehler: string | null = null;
  for (const datei of neue) {
    const problem = dateiProbleme([...dateien, datei]);
    if (problem) fehler ??= problem;
    else dateien = [...dateien, datei];
  }
  return { dateien, fehler };
}

export const erfolgsSatz = (referenz: string, mitMail: boolean) => (mitMail ? erfolgMitMail(referenz) : erfolgOhneMail(referenz));

/** F-23 (PF-NB-hilfe-3, Fehlersuche Nr. 34): der Name einer Seite für den Satz „Mitgeschickt werden …“; unbekannt: null. */
export function seitenName(pfad: string | null | undefined): string | null {
  if (!pfad || pfad.length > 300 || !SEITE.test(pfad)) return null;
  const unter = (wurzel: string) => pfad === wurzel || pfad.startsWith(`${wurzel}/`);
  const S = TICKET_TEXTE.seiten;
  if (pfad === '/') return S.start;
  if (unter('/ris')) return S.recherche;
  if (unter('/vorlagen')) return S.vorlagen;
  if (unter('/einstellungen/organisation')) return EBENEN.adminEinstellungen;
  if (unter('/einstellungen')) return EBENEN.meineEinstellungen;
  if (unter('/hilfe')) return MENUE.hilfeUndFeedback;
  if (unter('/ki-cockpit')) return MENUE.kiCockpit;
  if (unter('/verwaltung')) return MENUE.plattformVerwaltung;
  return null;
}
/** „Mitgeschickt werden: …“; ein Pfad nur, wenn er zum Muster des Servers passt, und dann mit seinem Namen (seitenName). */
export const mitgeschicktSatz = (pfad: string | null | undefined, code: string | null | undefined, produkt: string, mitRecherche = false) =>
  mitgeschickt(seitenName(pfad), code ?? null, produkt, mitRecherche);

/** Die Version für den Server (Muster und Länge wie dort), sonst nichts. */
export const versionFuerServer = (version: string | undefined) => (version && version.length <= 40 && /^[A-Za-z0-9._+-]+$/.test(version) ? version : undefined);

const DATUM = new Intl.DateTimeFormat('de-AT', { timeZone: 'Europe/Vienna', day: '2-digit', month: '2-digit', year: 'numeric' });
/** TT.MM.JJJJ nach Wiener Zeit; ungültig leer. */
export function datum(iso: string): string {
  const zeit = new Date(iso);
  return Number.isNaN(zeit.getTime()) ? '' : DATUM.format(zeit);
}

// PF-310-2: Detail einer Anfrage.

/** Passt die Referenz zum Muster des Servers? Sonst zeigt das Detail ohne Anfrage den Satz für „nicht sichtbar“. */
export const istReferenz = (text: string) => TICKET_REFERENZ.test(text);

/** Das Formular mit „Folgeanfrage zu {SUP-…}“ im Betreff (vorbelegung, folge). */
export const folgeAdresse = (referenz: string) => `/hilfe/tickets/neu?folge=${encodeURIComponent(referenz)}`;

const DATUM_ZEIT = new Intl.DateTimeFormat('de-AT', { timeZone: 'Europe/Vienna', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit',
  minute: '2-digit', hourCycle: 'h23' });
/** „TT.MM.JJJJ, hh:mm“ nach Wiener Zeit; ungültig leer. */
export function datumZeit(iso: string): string {
  const zeit = new Date(iso);
  return Number.isNaN(zeit.getTime()) ? '' : DATUM_ZEIT.format(zeit);
}

export type AbsenderZeile = { art: 'eigen' | 'person' | 'system' | 'verlauf'; titel: string | null };
/** Wer eine Nachricht schrieb: „Sie“ bei eigenen, sonst der Name vom Server („{Vorname} vom Support“, „{Name} · Verwaltung
 *  {Firma}“, „Gelöschtes Konto“); Systemzeilen ohne Absender; ein angehängtes Gespräch mit seiner ersten Zeile als Titel. */
export function absenderZeile(nachricht: Pick<TicketNachricht, 'von' | 'eigen' | 'name' | 'text'>): AbsenderZeile {
  const D = TICKET_TEXTE.detail;
  if (nachricht.von === 'SYSTEM') return { art: 'system', titel: null };
  if (nachricht.von === 'VERLAUF') return { art: 'verlauf', titel: verlaufTeile(nachricht.text).titel };
  if (nachricht.eigen) return { art: 'eigen', titel: D.sie };
  return { art: 'person', titel: nachricht.name?.trim() || D.geloeschtesKonto };
}

/** Ein angehängtes Gespräch: erste Zeile als Titel (sonst „Gespräch mit dem Hilfe-Assistenten“), der Rest als Inhalt. */
export function verlaufTeile(text: string): { titel: string; rest: string } {
  const zeilen = text.replace(/\r\n?/g, '\n').split('\n');
  const titel = zeilen[0]?.trim() || TICKET_TEXTE.detail.verlaufTitel;
  return { titel, rest: zeilen.slice(1).join('\n').replace(/^\s*\n/, '').trimEnd() };
}

/** „Der Support wartet seit {Datum} auf Ihre Antwort. Ohne Antwort schließen wir die Anfrage am {Datum} als gelöst.“ */
export const wartetSatz = (seit: string, geloestAm: string | null) => wartetSeit(datum(seit), geloestAm ? datum(geloestAm) || null : null);

export const sichtbarkeitSatz = (wert: string) => (wert === 'FIRMA' ? TICKET_TEXTE.detail.sichtbarFirma : TICKET_TEXTE.detail.sichtbarPrivat);

/** Die fünf Stufen der Zufriedenheit (Werte 1 bis 5). */
export const bewertungsOptionen = () => TICKET_TEXTE.detail.bewertungen.map((text, i) => ({ wert: i + 1, text }));

export type AnzeigeAktionen = TicketAktionen & { bewerten: boolean };
/** Was die Seite anbietet: was der Server in aktionen erlaubt, dazu Bewerten; in einer Sicht nichts. Antworten nie bei „Gelöst“
 *  oder „Geschlossen“ (dort bestätigen, wieder öffnen bzw. neue Anfrage dazu). */
export function aktionenFuerAnzeige(ticket: Pick<TicketAnsicht, 'status' | 'aktionen' | 'bewertung'>, sicht: boolean): AnzeigeAktionen {
  const a = ticket.aktionen;
  if (sicht) return { antworten: false, bestaetigen: false, wiederOeffnen: false, zurueckziehen: false, sichtbarkeitAendern: false, folgen: false,
    neueAnfrageDazu: false, bewerten: false };
  const geloest = ticket.status === 'GELOEST', geschlossen = ticket.status === 'GESCHLOSSEN';
  return { ...a, antworten: a.antworten && !geloest && !geschlossen, bestaetigen: a.bestaetigen && geloest, wiederOeffnen: a.wiederOeffnen && geloest,
    zurueckziehen: a.zurueckziehen && !geloest && !geschlossen, neueAnfrageDazu: a.neueAnfrageDazu && geschlossen, bewerten: !!ticket.bewertung?.offen };
}

/** Der Satz einer Fehlerantwort des Servers ({ error }), sonst null. */
export const serverSatz = (daten: Record<string, unknown> | null) => (typeof daten?.error === 'string' && daten.error.trim() ? daten.error : null);

/** Antwort (1 bis 10.000 Zeichen, ohne Steuerzeichen): leer null (das nennt „Es fehlt noch“), sonst der Fehler am Feld oder null. */
export function antwortFehler(text: string): string | null {
  if (!text.trim()) return null;
  if (STEUERZEICHEN.test(text)) return TICKET_TEXTE.fehler.steuerzeichen;
  return text.trim().length > BESCHREIBUNG.max ? TICKET_TEXTE.detail.antwortLaenge : null;
}

// PF-316 (BAU-HILFE.md A-10; TEIL-3 SP3-01 „Weitere Einstiege“): der Weg vom Fehlerhinweis der Recherche ins Formular.

/** Der Fehlercode für den Link unter einem Fehler der Recherche, sonst null (kein Link). Recherchesperren übernimmt die
 *  Sperrseite; ein Code aus FEHLERCODES bleibt; 5xx, Netzfehler, Zeitüberschreitung und Abbruch ohne Antwort gelten als
 *  RESEARCH_FAILED; 4xx mit fremdem oder ohne Code bekommt keinen Link. */
export function meldeCode(error: unknown): Fehlercode | null {
  if (istRechercheSperre(error)) return null;
  const e = error as { response?: { status?: unknown; data?: { code?: unknown } | null } } | null | undefined;
  const code = e?.response?.data?.code;
  if (typeof code === 'string' && (FEHLERCODES as readonly string[]).includes(code)) return code as Fehlercode;
  const status = e?.response?.status;
  if (typeof status === 'number') return status >= 500 ? 'RESEARCH_FAILED' : null;
  // Ohne Antwort des Servers: ERR_NETWORK, ECONNREFUSED, ECONNABORTED oder ein Abbruch der Recherche.
  return error ? 'RESEARCH_FAILED' : null;
}

/** /hilfe/tickets/neu?seite=…&fehlercode=… (seite nur mit gültigem Pfad bis 300 Zeichen, ohne Abfrage und Anker). */
export function problemMeldenAdresse(seite: string | null | undefined, code: string): string {
  const pfad = (seite ?? '').split(/[?#]/)[0];
  const teile = [...(pfad.length <= 300 && SEITE.test(pfad) ? [`seite=${encodeURIComponent(pfad)}`] : []), `fehlercode=${encodeURIComponent(code)}`];
  return `/hilfe/tickets/neu?${teile.join('&')}`;
}

/** F-23 (PF-NB-hilfe-3): /hilfe/tickets/neu?kategorie=ANTWORT_FALSCH&recherche=<uuid>&seite=… (seite nur mit gültigem Pfad). */
export function antwortMeldenAdresse(rechercheId: string, seite: string | null | undefined): string {
  const pfad = (seite ?? '').split(/[?#]/)[0];
  const teile = ['kategorie=ANTWORT_FALSCH', `recherche=${encodeURIComponent(rechercheId)}`,
    ...(pfad.length <= 300 && SEITE.test(pfad) ? [`seite=${encodeURIComponent(pfad)}`] : [])];
  return `/hilfe/tickets/neu?${teile.join('&')}`;
}

/** Feld, an dem ein Fehler des Servers (400 VALIDATION, fields) steht; null: unter den Knöpfen. */
export function feldDesServers(fields: unknown): 'kategorie' | 'betreff' | 'beschreibung' | null {
  const liste = Array.isArray(fields) ? fields.filter((f): f is string => typeof f === 'string') : [];
  return (['kategorie', 'betreff', 'beschreibung'] as const).find(feld => liste.includes(feld)) ?? null;
}
